<template>
  <div class="health">
    <header class="section-header">
      <div>
        <h1>Salud</h1>
        <p class="muted">
          Organiza tus historias médicas y registra cada visita con documentos y detalles.
        </p>
      </div>
      <button class="primary" @click="openHistoryModal()">+ Nueva historia</button>
    </header>

    <section v-if="loading" class="health-grid skeleton-health" role="status" aria-live="polite">
      <span class="sr-only">Cargando historias...</span>
      <aside class="history-list">
        <div class="history-list-header">
          <div class="skeleton skeleton-line lg" style="width: 46%"></div>
        </div>
        <div class="history-items">
          <div v-for="n in 4" :key="`hist-skel-${n}`" class="history-item">
            <div class="skeleton skeleton-line" style="width: 72%"></div>
            <div class="skeleton skeleton-line" style="width: 48%"></div>
          </div>
        </div>
      </aside>
      <section class="visit-panel">
        <div class="visit-header">
          <div class="skeleton-stack">
            <div class="skeleton skeleton-line lg" style="width: 50%"></div>
            <div class="skeleton skeleton-line" style="width: 60%"></div>
          </div>
          <div class="visit-actions">
            <div class="skeleton skeleton-line" style="width: 110px; height: 30px"></div>
            <div class="skeleton skeleton-line" style="width: 110px; height: 30px"></div>
            <div class="skeleton skeleton-line" style="width: 120px; height: 30px"></div>
          </div>
        </div>
        <div class="visit-grid">
          <div v-for="n in 3" :key="`visit-skel-${n}`" class="visit-card">
            <div class="skeleton skeleton-line lg" style="width: 54%"></div>
            <div class="skeleton skeleton-line" style="width: 46%"></div>
            <div class="skeleton skeleton-line" style="width: 58%"></div>
            <div class="skeleton skeleton-line" style="width: 40%"></div>
          </div>
        </div>
      </section>
    </section>
    <section v-if="error" class="state error">{{ error }}</section>

    <section v-if="!loading" class="health-grid">
      <aside class="history-list">
        <div class="history-list-header">
          <h2>Historias</h2>
        </div>
        <div class="history-items">
          <button
            v-for="history in histories"
            :key="history.id"
            class="history-item"
            :class="{ active: history.id === selectedHistoryId }"
            @click="selectHistory(history)"
          >
            <strong>{{ history.title }}</strong>
            <span v-if="history.description" class="muted small">{{ history.description }}</span>
          </button>
          <div v-if="!histories.length" class="empty-card">
            <p>No hay historias todavía.</p>
          </div>
        </div>
      </aside>

      <section class="visit-panel">
        <div class="visit-header">
          <div>
            <h2>{{ selectedHistory?.title || "Selecciona una historia" }}</h2>
            <p v-if="selectedHistory?.description" class="muted">
              {{ selectedHistory.description }}
            </p>
          </div>
          <div class="visit-actions">
            <button class="ghost" @click="openHistoryModal(selectedHistory)" :disabled="!selectedHistory">
              Editar historia
            </button>
            <button
              class="ghost danger"
              @click="removeHistory(selectedHistory)"
              :disabled="!selectedHistory"
            >
              Eliminar historia
            </button>
            <button class="primary" @click="openVisitModal()" :disabled="!selectedHistory">
              + Nueva visita
            </button>
          </div>
        </div>

        <div v-if="visitsLoading" class="visit-grid skeleton-visits" role="status" aria-live="polite">
          <span class="sr-only">Cargando visitas...</span>
          <div v-for="n in 3" :key="`visit-skel-inline-${n}`" class="visit-card">
            <div class="skeleton skeleton-line lg" style="width: 52%"></div>
            <div class="skeleton skeleton-line" style="width: 44%"></div>
            <div class="skeleton skeleton-line" style="width: 60%"></div>
            <div class="skeleton skeleton-line" style="width: 38%"></div>
          </div>
        </div>
        <div v-if="visitsError" class="state error">{{ visitsError }}</div>

        <div v-if="!visitsLoading" class="visit-grid">
          <div v-for="visit in visits" :key="visit.id" class="visit-card">
            <div class="visit-head">
              <div>
                <h3>{{ formatDate(visit.visitDate) }}</h3>
                <p class="muted small">{{ visit.specialty || "Especialidad no indicada" }}</p>
                <p class="muted small">{{ visit.center || "Centro no indicado" }}</p>
                <p v-if="visit.location" class="muted small">{{ visit.location }}</p>
              </div>
              <div class="visit-actions">
                <button class="ghost" @click="openVisitModal(visit)">Editar</button>
                <button class="ghost danger" @click="removeVisit(visit)">Eliminar</button>
              </div>
            </div>
            <p v-if="visit.notes" class="notes">{{ visit.notes }}</p>

            <div v-if="visit.files?.length" class="document-files">
              <a
                v-for="file in visit.files"
                :key="file.id"
                class="file-chip"
                :href="file.data"
                target="_blank"
                rel="noreferrer"
              >
                {{ file.name || "Documento" }}
              </a>
            </div>
          </div>
          <div v-if="!visits.length" class="empty-card">
            <p>Sin visitas registradas.</p>
          </div>
        </div>
      </section>
    </section>

    <div v-if="showHistoryModal" class="modal-backdrop" @click.self="closeHistoryModal">
      <div class="modal">
        <header>
          <h3>{{ historyForm.id ? "Editar historia" : "Nueva historia" }}</h3>
          <button class="icon danger" @click="closeHistoryModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Título
            <input v-model="historyForm.title" type="text" placeholder="Accidente coche Junio 2026" />
          </label>
          <label>
            Descripción
            <textarea v-model="historyForm.description" rows="3" placeholder="Notas generales"></textarea>
          </label>
          <p v-if="historyFormError" class="form-error">{{ historyFormError }}</p>
        </div>
        <footer>
          <button class="ghost" @click="closeHistoryModal">Cancelar</button>
          <button class="primary" @click="saveHistory">{{ historyForm.id ? "Guardar" : "Crear" }}</button>
        </footer>
      </div>
    </div>

    <div v-if="showVisitModal" class="modal-backdrop" @click.self="closeVisitModal">
      <div class="modal">
        <header>
          <h3>{{ visitForm.id ? "Editar visita" : "Nueva visita" }}</h3>
          <button class="icon danger" @click="closeVisitModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Fecha
            <input v-model="visitForm.visitDate" type="date" />
          </label>
          <div class="row">
            <label>
              Especialidad
              <input v-model="visitForm.specialty" type="text" placeholder="Traumatología" />
            </label>
            <label>
              Centro
              <input v-model="visitForm.center" type="text" placeholder="Clínica/ Hospital" />
            </label>
          </div>
          <label>
            Localización
            <input v-model="visitForm.location" type="text" placeholder="Madrid" />
          </label>
          <label>
            Notas
            <textarea v-model="visitForm.notes" rows="3" placeholder="Detalles de la visita"></textarea>
          </label>
          <label>
            Documentos
            <input ref="visitFilesInput" type="file" multiple @change="onVisitFilesSelected" />
          </label>
          <div v-if="selectedVisitFiles.length" class="ticket-preview">
            <p class="muted">Documentos nuevos</p>
            <div class="ticket-preview-grid">
              <div v-for="(file, index) in selectedVisitFiles" :key="file.id" class="ticket-preview-item">
                <img
                  v-if="file.preview"
                  :src="file.preview"
                  :alt="file.name"
                  @click="openLightbox(file.preview, file.name)"
                />
                <div v-else class="file-fallback">{{ file.name }}</div>
                <button class="ticket-remove" @click.stop="removePendingVisitFile(index)">×</button>
              </div>
            </div>
          </div>
          <div v-if="existingVisitFiles.length" class="ticket-preview">
            <p class="muted">Documentos existentes</p>
            <div class="ticket-preview-grid">
              <div v-for="file in existingVisitFiles" :key="file.id" class="ticket-preview-item">
                <img
                  v-if="file.type === 'IMAGE'"
                  :src="file.data"
                  :alt="file.name"
                  @click="openLightbox(file.data, file.name)"
                />
                <div v-else class="file-fallback">{{ file.name }}</div>
                <button class="ticket-remove" @click.stop="removeExistingVisitFile(visitForm.id, file.id)">×</button>
              </div>
            </div>
          </div>
          <p v-if="visitFormError" class="form-error">{{ visitFormError }}</p>
        </div>
        <footer>
          <button class="ghost" @click="closeVisitModal">Cancelar</button>
          <button class="primary" @click="saveVisit">{{ visitForm.id ? "Guardar" : "Crear" }}</button>
        </footer>
      </div>
    </div>

    <div v-if="lightboxSrc" class="lightbox" @click.self="closeLightbox">
      <button class="lightbox-close" @click="closeLightbox">×</button>
      <img :src="lightboxSrc" :alt="lightboxAlt" />
    </div>
  </div>
</template>

<script>
import {
  fetchHealthHistories,
  createHealthHistory,
  updateHealthHistory,
  deleteHealthHistory,
  fetchHealthVisits,
  createHealthVisit,
  updateHealthVisit,
  deleteHealthVisit,
  deleteHealthVisitFile,
} from "../services/health";

export default {
  name: "HealthView",
  data() {
    return {
      histories: [],
      visits: [],
      selectedHistoryId: null,
      loading: false,
      error: "",
      visitsLoading: false,
      visitsError: "",
      showHistoryModal: false,
      showVisitModal: false,
      historyFormError: "",
      visitFormError: "",
      historyForm: this.emptyHistoryForm(),
      visitForm: this.emptyVisitForm(),
      selectedVisitFiles: [],
      existingVisitFiles: [],
      lightboxSrc: "",
      lightboxAlt: "",
    };
  },
  async mounted() {
    await this.loadHistories();
  },
  computed: {
    selectedHistory() {
      return this.histories.find((item) => item.id === this.selectedHistoryId) || null;
    },
  },
  methods: {
    emptyHistoryForm() {
      return {
        id: null,
        title: "",
        description: "",
      };
    },
    emptyVisitForm() {
      return {
        id: null,
        visitDate: new Date().toISOString().slice(0, 10),
        specialty: "",
        center: "",
        location: "",
        notes: "",
      };
    },
    async loadHistories() {
      this.loading = true;
      this.error = "";
      try {
        const data = await fetchHealthHistories();
        this.histories = data || [];
        if (!this.selectedHistoryId && this.histories.length) {
          this.selectHistory(this.histories[0]);
        }
      } catch (err) {
        this.error = err?.message || "No se pudieron cargar las historias.";
      } finally {
        this.loading = false;
      }
    },
    async loadVisits(historyId) {
      if (!historyId) {
        this.visits = [];
        return;
      }
      this.visitsLoading = true;
      this.visitsError = "";
      try {
        const data = await fetchHealthVisits(historyId);
        this.visits = data || [];
      } catch (err) {
        this.visitsError = err?.message || "No se pudieron cargar las visitas.";
      } finally {
        this.visitsLoading = false;
      }
    },
    selectHistory(history) {
      this.selectedHistoryId = history.id;
      this.loadVisits(history.id);
    },
    openHistoryModal(history) {
      this.historyFormError = "";
      if (history) {
        this.historyForm = {
          id: history.id,
          title: history.title,
          description: history.description || "",
        };
      } else {
        this.historyForm = this.emptyHistoryForm();
      }
      this.showHistoryModal = true;
    },
    closeHistoryModal() {
      this.showHistoryModal = false;
      this.historyFormError = "";
    },
    async saveHistory() {
      const title = this.historyForm.title.trim();
      if (!title) {
        this.historyFormError = "El título es obligatorio.";
        return;
      }
      try {
        const payload = { title, description: this.historyForm.description || null };
        if (this.historyForm.id) {
          await updateHealthHistory(this.historyForm.id, payload);
        } else {
          await createHealthHistory(payload);
        }
        await this.loadHistories();
        this.closeHistoryModal();
      } catch (err) {
        this.historyFormError = err?.message || "No se pudo guardar la historia.";
      }
    },
    async removeHistory(history) {
      if (!history) return;
      if (!confirm(`¿Eliminar "${history.title}"?`)) return;
      try {
        await deleteHealthHistory(history.id);
        this.histories = this.histories.filter((item) => item.id !== history.id);
        if (this.selectedHistoryId === history.id) {
          this.selectedHistoryId = this.histories[0]?.id || null;
          await this.loadVisits(this.selectedHistoryId);
        }
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar la historia.";
      }
    },
    openVisitModal(visit) {
      this.visitFormError = "";
      this.resetPendingVisitFiles();
      if (this.$refs.visitFilesInput) {
        this.$refs.visitFilesInput.value = "";
      }
      if (visit) {
        this.visitForm = {
          id: visit.id,
          visitDate: visit.visitDate,
          specialty: visit.specialty || "",
          center: visit.center || "",
          location: visit.location || "",
          notes: visit.notes || "",
        };
        this.existingVisitFiles = visit.files ? [...visit.files] : [];
      } else {
        this.visitForm = this.emptyVisitForm();
        this.existingVisitFiles = [];
      }
      this.showVisitModal = true;
    },
    closeVisitModal() {
      this.showVisitModal = false;
      this.visitFormError = "";
      this.resetPendingVisitFiles();
      this.existingVisitFiles = [];
    },
    async saveVisit() {
      if (!this.selectedHistoryId) return;
      const visitDate = this.visitForm.visitDate;
      if (!visitDate) {
        this.visitFormError = "La fecha es obligatoria.";
        return;
      }
      try {
        const files = await this.buildVisitFiles();
        const payload = {
          visitDate,
          specialty: this.visitForm.specialty || null,
          center: this.visitForm.center || null,
          location: this.visitForm.location || null,
          notes: this.visitForm.notes || null,
          files: files.length ? files : null,
        };
        if (this.visitForm.id) {
          await updateHealthVisit(this.selectedHistoryId, this.visitForm.id, payload);
        } else {
          await createHealthVisit(this.selectedHistoryId, payload);
        }
        await this.loadVisits(this.selectedHistoryId);
        this.closeVisitModal();
      } catch (err) {
        this.visitFormError = err?.message || "No se pudo guardar la visita.";
      }
    },
    async removeVisit(visit) {
      if (!visit || !this.selectedHistoryId) return;
      if (!confirm("¿Eliminar esta visita?")) return;
      try {
        await deleteHealthVisit(this.selectedHistoryId, visit.id);
        this.visits = this.visits.filter((item) => item.id !== visit.id);
      } catch (err) {
        this.visitsError = err?.message || "No se pudo eliminar la visita.";
      }
    },
    onVisitFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const mapped = files.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        name: file.name,
        file,
        preview: file.type?.startsWith("image/") ? URL.createObjectURL(file) : "",
      }));
      this.selectedVisitFiles = [...this.selectedVisitFiles, ...mapped];
      event.target.value = "";
    },
    removePendingVisitFile(index) {
      const [removed] = this.selectedVisitFiles.splice(index, 1);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
    },
    resetPendingVisitFiles() {
      this.selectedVisitFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      this.selectedVisitFiles = [];
    },
    async removeExistingVisitFile(visitId, resourceId) {
      if (!this.selectedHistoryId || !visitId) return;
      try {
        await deleteHealthVisitFile(this.selectedHistoryId, visitId, resourceId);
        this.existingVisitFiles = this.existingVisitFiles.filter((file) => file.id !== resourceId);
        this.visits = this.visits.map((item) =>
          item.id === visitId
            ? { ...item, files: (item.files || []).filter((file) => file.id !== resourceId) }
            : item
        );
      } catch (err) {
        this.visitFormError = err?.message || "No se pudo eliminar el documento.";
      }
    },
    async buildVisitFiles() {
      if (!this.selectedVisitFiles.length) return [];
      const mapped = await Promise.all(
        this.selectedVisitFiles.map(async (item) => ({
          name: item.name,
          type: item.file.type?.startsWith("image/") ? "IMAGE" : "FILE",
          format: "BASE64",
          data: await this.fileToBase64(item.file),
        }))
      );
      return mapped;
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    formatDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(new Date(value));
    },
    openLightbox(src, alt) {
      if (!src) return;
      this.lightboxSrc = src;
      this.lightboxAlt = alt || "Imagen";
    },
    closeLightbox() {
      this.lightboxSrc = "";
      this.lightboxAlt = "";
    },
  },
};
</script>

<style scoped>
.health {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.section-header h1 {
  margin: 0 0 6px;
}

.health-grid {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  gap: 20px;
}

.history-list {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  display: grid;
  gap: 12px;
  box-shadow: var(--shadow);
  height: fit-content;
}

.history-list-header h2 {
  margin: 0;
}

.history-items {
  display: grid;
  gap: 10px;
}

.history-item {
  background: var(--panel-2);
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 10px 12px;
  color: var(--text);
  display: grid;
  gap: 4px;
  text-align: left;
  cursor: pointer;
}

.history-item.active {
  border-color: rgba(56, 189, 248, 0.45);
  background: rgba(56, 189, 248, 0.15);
}

.visit-panel {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  display: grid;
  gap: 16px;
  box-shadow: var(--shadow);
}

.visit-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  align-items: flex-start;
}

.visit-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.visit-grid {
  display: grid;
  gap: 12px;
}

.visit-card {
  background: var(--panel-2);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  padding: 14px;
  display: grid;
  gap: 10px;
}

.visit-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.visit-head h3 {
  margin: 0;
}

.notes {
  margin: 0;
  color: var(--muted);
}

.document-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-chip {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.primary {
  background: linear-gradient(120deg, var(--accent), #22c55e);
  color: #071022;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  min-width: 140px;
}

.ghost {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 8px 12px;
  border-radius: 999px;
  cursor: pointer;
  min-width: 120px;
  text-align: center;
}

.ghost.danger {
  color: var(--danger);
  border-color: rgba(248, 113, 113, 0.4);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(520px, 100%);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 20px;
  display: grid;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  overflow-x: hidden;
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
}

.modal header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  display: grid;
  gap: 12px;
}

.modal-body label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 14px;
  min-width: 0;
}

.modal-body input,
.modal-body textarea,
.modal-body select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  width: 100%;
  max-width: 100%;
}

.modal-body input[type="file"] {
  display: block;
  min-width: 0;
}

.row {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.ticket-preview {
  display: grid;
  gap: 8px;
}

.ticket-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.ticket-preview-item {
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.35);
  overflow: hidden;
  display: grid;
  place-items: center;
}

.ticket-preview-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
  display: block;
}

.ticket-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  cursor: pointer;
}

.file-fallback {
  font-size: 12px;
  color: var(--muted);
  text-align: center;
  padding: 12px 8px;
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.form-error {
  margin: 0;
  color: var(--danger);
  font-size: 13px;
}

.empty-card {
  text-align: center;
  color: var(--muted);
}

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(3, 6, 14, 0.85);
  display: grid;
  place-items: center;
  z-index: 40;
  padding: 24px;
}

.lightbox img {
  max-width: min(920px, 92vw);
  max-height: min(90vh, 720px);
  object-fit: contain;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.65);
  padding: 12px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
}

.lightbox-close {
  position: absolute;
  top: 18px;
  right: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
}

.muted {
  color: var(--muted);
}

.small {
  font-size: 12px;
}

@media (max-width: 980px) {
  .health-grid {
    grid-template-columns: 1fr;
  }
}
</style>
